import { useEffect } from 'react';
import { SITE_URL, absoluteUrl, alternateUrls, localizedPath } from './langPath';

const OG_IMAGE = `${SITE_URL}/og-image.png`;

// `path` (language-neutral, e.g. '/about') derives canonical + uk/ru hreflang
// alternates automatically; `canonical`/`hreflangPaths` remain for one-off pages.
// `noindex` is for 404 states.
export function usePageMeta({ title, description, canonical: canonicalProp, lang = 'uk', keywords, image, hreflangPaths: hreflangProp, path, noindex = false }) {
  useEffect(() => {
    const canonical = path ? absoluteUrl(localizedPath(path, lang)) : canonicalProp;
    const hreflangPaths = path ? alternateUrls(path) : hreflangProp;

    const created = [];
    const restored = [];

    // Upsert a meta/link tag: reuse an existing element (e.g. the static
    // defaults baked into public/index.html) instead of appending a
    // duplicate, so crawlers that read the first match don't see stale
    // homepage values on a tool/landing page.
    const setTag = (selector, tag, attrs, valueAttr) => {
      let el = document.querySelector(selector);
      if (el) {
        const prevValue = el.getAttribute(valueAttr);
        restored.push(() => el.setAttribute(valueAttr, prevValue ?? ''));
      } else {
        el = document.createElement(tag);
        Object.entries(attrs).forEach(([k, v]) => {
          if (k !== valueAttr) el.setAttribute(k, v);
        });
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute(valueAttr, attrs[valueAttr]);
      return el;
    };

    // Prerendered HTML already contains every tag below, so all of them are
    // upserted — appending would duplicate canonical/hreflang after hydration.
    const removeTag = (selector) => {
      const el = document.querySelector(selector);
      if (!el) return;
      const parent = el.parentNode;
      const next = el.nextSibling;
      el.remove();
      restored.push(() => parent?.insertBefore(el, next));
    };

    const prevTitle = document.title;
    document.title = title;

    const prevLang = document.documentElement.lang;
    document.documentElement.lang = lang === 'ru' ? 'ru' : 'uk';

    setTag('meta[name="description"]', 'meta', { name: 'description', content: description }, 'content');

    const ogImage = image || OG_IMAGE;

    if (keywords) setTag('meta[name="keywords"]', 'meta', { name: 'keywords', content: keywords }, 'content');
    else removeTag('meta[name="keywords"]');
    setTag('meta[name="robots"]', 'meta', {
      name: 'robots',
      content: noindex
        ? 'noindex, follow'
        : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
    }, 'content');
    if (canonical && !noindex) setTag('link[rel="canonical"]', 'link', { rel: 'canonical', href: canonical }, 'href');
    else removeTag('link[rel="canonical"]');
    ['uk', 'ru', 'x-default'].forEach((hl) => {
      const selector = `link[rel="alternate"][hreflang="${hl}"]`;
      if (hreflangPaths && !noindex) {
        const href = hl === 'ru' ? hreflangPaths.ru : hreflangPaths.uk;
        setTag(selector, 'link', { rel: 'alternate', hreflang: hl, href }, 'href');
      } else {
        removeTag(selector);
      }
    });
    setTag('meta[property="og:title"]', 'meta', { property: 'og:title', content: title }, 'content');
    setTag('meta[property="og:description"]', 'meta', { property: 'og:description', content: description }, 'content');
    if (canonical) setTag('meta[property="og:url"]', 'meta', { property: 'og:url', content: canonical }, 'content');
    setTag('meta[property="og:type"]', 'meta', { property: 'og:type', content: 'website' }, 'content');
    setTag('meta[property="og:image"]', 'meta', { property: 'og:image', content: ogImage }, 'content');
    setTag('meta[property="og:image:width"]', 'meta', { property: 'og:image:width', content: '1200' }, 'content');
    setTag('meta[property="og:image:height"]', 'meta', { property: 'og:image:height', content: '630' }, 'content');
    setTag('meta[property="og:locale"]', 'meta', { property: 'og:locale', content: lang === 'ru' ? 'ru_UA' : 'uk_UA' }, 'content');
    setTag('meta[property="og:locale:alternate"]', 'meta', { property: 'og:locale:alternate', content: lang === 'ru' ? 'uk_UA' : 'ru_UA' }, 'content');
    setTag('meta[property="og:site_name"]', 'meta', { property: 'og:site_name', content: 'SmarTOOL' }, 'content');
    setTag('meta[name="twitter:card"]', 'meta', { name: 'twitter:card', content: 'summary_large_image' }, 'content');
    setTag('meta[name="twitter:title"]', 'meta', { name: 'twitter:title', content: title }, 'content');
    setTag('meta[name="twitter:description"]', 'meta', { name: 'twitter:description', content: description }, 'content');
    setTag('meta[name="twitter:image"]', 'meta', { name: 'twitter:image', content: ogImage }, 'content');

    return () => {
      document.title = prevTitle;
      document.documentElement.lang = prevLang;
      restored.reverse().forEach((fn) => fn());
      created.forEach((el) => el?.remove());
    };
  }, [title, description, canonicalProp, lang, keywords, image, hreflangProp, path, noindex]);
}
